import { computed, ref } from "vue";

const defaultSortIcons = {
  neutral: "sort",
  asc: "sort-alpha-up",
  desc: "sort-alpha-down",
};

const collator = new Intl.Collator("es", {
  numeric: true,
  sensitivity: "base",
});

const getNestedValue = (item, path) => {
  if (typeof path === "function") {
    return path(item);
  }

  if (!path) {
    return "";
  }

  return path.split(".").reduce((value, key) => value?.[key], item);
};

const normalizeValue = (value) => {
  if (value === null || typeof value === "undefined") {
    return "";
  }

  if (typeof value === "number") {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  return String(value);
};

export function useTableSort({ defaultSort = null, sortIcons = defaultSortIcons } = {}) {
  const sortField = ref("");
  const sortDirection = ref("");

  const resolvedSortField = computed(() => sortField.value || defaultSort?.field || "");
  const resolvedSortDirection = computed(() => sortDirection.value || defaultSort?.direction || "");

  const toggleSort = (field) => {
    if (sortField.value !== field) {
      sortField.value = field;
      sortDirection.value = "ASC";
      return;
    }

    if (sortDirection.value === "ASC") {
      sortDirection.value = "DESC";
      return;
    }

    sortField.value = "";
    sortDirection.value = "";
  };

  const clearSort = () => {
    sortField.value = "";
    sortDirection.value = "";
  };

  const getSortIcon = (field) => {
    if (resolvedSortField.value !== field) {
      return sortIcons.neutral;
    }

    return resolvedSortDirection.value === "ASC" ? sortIcons.asc : sortIcons.desc;
  };

  const applySortParams = (params = {}) => {
    if (!resolvedSortField.value || !resolvedSortDirection.value) {
      return params;
    }

    if (typeof resolvedSortField.value === "function") {
      return params;
    }

    params[`order[${resolvedSortField.value}]`] = resolvedSortDirection.value;
    return params;
  };

  const sortItemsLocally = (items) => {
    if (!Array.isArray(items)) {
      return [];
    }

    if (!resolvedSortField.value || !resolvedSortDirection.value) {
      return [...items];
    }

    return [...items].sort((firstItem, secondItem) => {
      const firstValue = normalizeValue(getNestedValue(firstItem, resolvedSortField.value));
      const secondValue = normalizeValue(getNestedValue(secondItem, resolvedSortField.value));

      if (firstValue === secondValue) {
        return 0;
      }

      const orderModifier = resolvedSortDirection.value === "ASC" ? 1 : -1;

      if (typeof firstValue === "number" && typeof secondValue === "number") {
        return firstValue > secondValue ? orderModifier : -orderModifier;
      }

      return collator.compare(String(firstValue), String(secondValue)) * orderModifier;
    });
  };

  return {
    applySortParams,
    clearSort,
    getSortIcon,
    sortField,
    sortDirection,
    sortItemsLocally,
    toggleSort,
  };
}
